/**
 * WEAPONS - dados das armas (secao 4.13 do ARCHITECTURE.md).
 *
 * Este arquivo e SO DADO. Nenhuma logica mora aqui: quem interpreta e o
 * WeaponSystem. Outros sistemas (HUD, ViewModel, Audio, Ballistics) podem ler
 * estes campos livremente, mas nunca devem escrever neles.
 *
 * ---------------------------------------------------------------------------
 * UNIDADES (leia antes de mexer em qualquer numero)
 * ---------------------------------------------------------------------------
 *   rpm             tiros por minuto. Intervalo entre tiros = 60 / rpm.
 *   damage          dano no torso, a queima-roupa, antes do multiplicador de hitbox.
 *   damageFalloff   { start, end, multiplier } em METROS. Dano cheio ate `start`,
 *                   cai linearmente ate `damage * multiplier` em `end`, e fica ai.
 *   spread.*        RADIANOS, meia-abertura do cone de dispersao. 0.0175 = 1 grau.
 *   recoilPattern   GRAUS por tiro, um item por bala do pente.
 *                     pitch > 0  -> a arma sobe (a mira vai para CIMA da tela)
 *                     yaw   > 0  -> a arma puxa para a DIREITA da tela
 *                   O WeaponSystem converte para radianos e inverte o sinal do yaw
 *                   ao acumular (no three, rotation.y positivo gira para a esquerda).
 *   recoilJitter    GRAUS, amplitude do ruido uniforme (+-) somado a cada passo do
 *                   padrao. Existe so para tirar o ar de robo: pequeno o bastante
 *                   para o padrao continuar decoravel.
 *   recoilRecovery  { speed, firingScale, delay, resetTime }
 *                     speed       constante da volta exponencial ao centro (1/s)
 *                     firingScale multiplica `speed` DURANTE a rajada. Quanto menor,
 *                                 mais o padrao se acumula (rajada longa fica dura).
 *                     delay       segundos segurando no topo depois da rajada
 *                     resetTime   segundos sem atirar para o padrao voltar ao tiro 1
 *   spreadPerShot   dispersao (rad) somada por tiro; teto em `spreadMax`.
 *   spreadRecovery  constante (1/s) da queda exponencial dessa dispersao acumulada.
 *   firstShotAccuracy  multiplicador de dispersao no PRIMEIRO tiro do padrao, com o
 *                   jogador no chao. E o que garante "primeiro tiro preciso".
 *   *RecoilScale    multiplicadores de recuo por estado do jogador.
 *   muzzleOffset    [x, y, z] em ESPACO LOCAL DA CAMERA (three olha para -Z).
 *                   Usado como boca do cano quando o ViewModel nao responder.
 *   penetration     0..1, capacidade de atravessar material fino (lido pela Ballistics).
 *   adsFov          FOV alvo da camera na mira, em graus.
 *   adsSensitivityScale  multiplicador de sensibilidade do mouse na mira.
 *
 * ---------------------------------------------------------------------------
 * IDENTIDADE DAS TRES ARMAS (o que cada numero esta tentando fazer sentir)
 * ---------------------------------------------------------------------------
 *   KR-7  fuzil de assalto  - a arma "certa". Sobe reto nos 5 primeiros tiros e
 *                             so entao comeca a serpentear. Na mira e cirurgica.
 *   VP-9  submetralhadora   - cospe balas. Dano baixo, cadencia alta, padrao que
 *                             corre para os lados em blocos de 3-4 tiros. Perdoa
 *                             movimento, nao perdoa distancia.
 *   SR-12 fuzil de precisao - 1 clique = 1 tiro. Primeiro tiro impecavel, coice
 *                             pesado, zoom forte. Duas balas no corpo, uma na cabeca.
 */

/** Fuzil de assalto. Espinha dorsal do arsenal: bom em tudo, otimo na mira. */
const KR7 = {
  id: 'kr7',
  name: 'KR-7',
  longName: 'FUZIL DE ASSALTO KR-7',
  class: 'assault',
  slot: 1,
  hasScope: false,

  fireMode: 'auto',
  rpm: 700,                       // 11.7 tiros/s -> pente inteiro em ~2.6 s
  damage: 26,                     // 4 tiros no torso a curta distancia
  damageFalloff: { start: 28, end: 62, multiplier: 0.62 },
  range: 140,
  pellets: 1,
  penetration: 0.5,

  magazine: 30,
  reserve: 180,
  reloadTime: 2.05,               // com bala na agulha
  reloadEmptyTime: 2.75,          // pente vazio: ainda precisa engatilhar

  switchTime: 0.55,
  sprintOutTime: 0.18,            // tempo para reerguer a arma ao sair da corrida
  adsTime: 0.22,
  adsFov: 52,
  adsSensitivityScale: 0.75,

  spread: {
    idle: 0.0200,                 // 1.15 grau no quadril: da para acertar perto, so
    walk: 0.0320,
    sprint: 0.0750,
    air: 0.1050,                  // 6 graus: atirar pulando e desistir do tiro
    crouch: 0.0140,
    ads: 0.0009                   // 0.05 grau: praticamente um ponto
  },
  spreadPerShot: 0.0016,
  spreadMax: 0.0120,
  spreadRecovery: 5.0,
  adsBloomScale: 0.55,            // a mira tambem segura a dispersao acumulada
  firstShotAccuracy: 0.40,

  /**
   * Padrao de recuo: 30 passos, um por bala.
   * Tiros 1-5 sobem quase reto (e o que torna a rajada curta letal e justa).
   * A partir do 6o entra a deriva lateral, que troca de lado em blocos longos -
   * de cima, o traco desenha um "S" alongado. Da para decorar; nao da para chutar.
   */
  recoilPattern: [
    { pitch: 0.85, yaw: 0.00 },
    { pitch: 0.95, yaw: 0.03 },
    { pitch: 1.00, yaw: -0.06 },
    { pitch: 1.02, yaw: 0.08 },
    { pitch: 0.98, yaw: 0.14 },
    { pitch: 0.90, yaw: 0.34 },
    { pitch: 0.82, yaw: 0.50 },
    { pitch: 0.74, yaw: 0.62 },
    { pitch: 0.66, yaw: 0.60 },
    { pitch: 0.60, yaw: 0.46 },
    { pitch: 0.55, yaw: 0.20 },
    { pitch: 0.52, yaw: -0.14 },
    { pitch: 0.50, yaw: -0.44 },
    { pitch: 0.48, yaw: -0.62 },
    { pitch: 0.46, yaw: -0.66 },
    { pitch: 0.45, yaw: -0.54 },
    { pitch: 0.44, yaw: -0.30 },
    { pitch: 0.43, yaw: 0.02 },
    { pitch: 0.42, yaw: 0.32 },
    { pitch: 0.42, yaw: 0.52 },
    { pitch: 0.41, yaw: 0.60 },
    { pitch: 0.40, yaw: 0.52 },
    { pitch: 0.40, yaw: 0.32 },
    { pitch: 0.39, yaw: 0.04 },
    { pitch: 0.38, yaw: -0.26 },
    { pitch: 0.38, yaw: -0.48 },
    { pitch: 0.37, yaw: -0.56 },
    { pitch: 0.37, yaw: -0.46 },
    { pitch: 0.36, yaw: -0.24 },
    { pitch: 0.36, yaw: 0.06 }
  ],
  recoilJitter: 0.035,
  recoilRecovery: { speed: 8.5, firingScale: 0.05, delay: 0.075, resetTime: 0.45 },

  kickScale: 1.00,                // coice visual instantaneo (vai para cameraRig.addRecoil)
  shake: { intensity: 0.045, duration: 0.06 },
  adsRecoilScale: 0.75,
  crouchRecoilScale: 0.85,
  airRecoilScale: 1.35,

  muzzleOffset: [0.16, -0.10, -0.55],
  muzzleScale: 1.00,              // tamanho do clarao pedido ao FX

  sound: {
    fire: 'kr7_fire',
    distant: 'kr7_fire_distant',
    dry: 'weapon_dry',
    reload: 'kr7_reload',
    reloadEmpty: 'kr7_reload_empty',
    reloadEnd: 'kr7_reload_end',
    switch: 'weapon_switch',
    adsIn: 'weapon_ads_in',
    adsOut: 'weapon_ads_out'
  }
};

/** Submetralhadora. Corredor, beco, escada: dentro de 20 metros ela ganha de tudo. */
const VP9 = {
  id: 'vp9',
  name: 'VP-9',
  longName: 'SUBMETRALHADORA VP-9',
  class: 'smg',
  slot: 2,
  hasScope: false,

  fireMode: 'auto',
  rpm: 950,                       // 15.8 tiros/s -> pente inteiro em ~2.0 s
  damage: 19,                     // 6 tiros no torso perto; longe nao mata nunca
  damageFalloff: { start: 12, end: 32, multiplier: 0.50 },
  range: 80,
  pellets: 1,
  penetration: 0.3,

  magazine: 32,
  reserve: 224,
  reloadTime: 1.75,
  reloadEmptyTime: 2.35,

  switchTime: 0.42,               // a mais rapida de sacar
  sprintOutTime: 0.12,
  adsTime: 0.17,
  adsFov: 58,
  adsSensitivityScale: 0.85,

  spread: {
    idle: 0.0170,                 // MELHOR no quadril que o fuzil: e a identidade dela
    walk: 0.0220,                 // andar quase nao piora: e a arma de movimento
    sprint: 0.0700,
    air: 0.1150,
    crouch: 0.0130,
    ads: 0.0042                   // 0.24 grau: mesmo na mira ela espalha
  },
  spreadPerShot: 0.0022,
  spreadMax: 0.0200,
  spreadRecovery: 6.5,
  adsBloomScale: 0.70,
  firstShotAccuracy: 0.75,

  /**
   * 32 passos. Pitch menor que o do fuzil, mas com cadencia 35% maior a subida
   * total e parecida - o que muda e o LADO: o padrao corre 3-4 tiros para um lado
   * e vira. Quem decora acerta; quem so segura o gatilho varre a parede.
   */
  recoilPattern: [
    { pitch: 0.62, yaw: 0.14 },
    { pitch: 0.72, yaw: 0.30 },
    { pitch: 0.74, yaw: 0.18 },
    { pitch: 0.70, yaw: -0.22 },
    { pitch: 0.66, yaw: -0.48 },
    { pitch: 0.62, yaw: -0.40 },
    { pitch: 0.58, yaw: 0.10 },
    { pitch: 0.56, yaw: 0.52 },
    { pitch: 0.54, yaw: 0.60 },
    { pitch: 0.52, yaw: 0.34 },
    { pitch: 0.50, yaw: -0.18 },
    { pitch: 0.49, yaw: -0.56 },
    { pitch: 0.48, yaw: -0.62 },
    { pitch: 0.47, yaw: -0.30 },
    { pitch: 0.46, yaw: 0.20 },
    { pitch: 0.45, yaw: 0.58 },
    { pitch: 0.45, yaw: 0.64 },
    { pitch: 0.44, yaw: 0.36 },
    { pitch: 0.43, yaw: -0.14 },
    { pitch: 0.43, yaw: -0.54 },
    { pitch: 0.42, yaw: -0.66 },
    { pitch: 0.42, yaw: -0.38 },
    { pitch: 0.41, yaw: 0.16 },
    { pitch: 0.41, yaw: 0.56 },
    { pitch: 0.40, yaw: 0.62 },
    { pitch: 0.40, yaw: 0.30 },
    { pitch: 0.39, yaw: -0.20 },
    { pitch: 0.39, yaw: -0.58 },
    { pitch: 0.38, yaw: -0.60 },
    { pitch: 0.38, yaw: -0.26 },
    { pitch: 0.37, yaw: 0.22 },
    { pitch: 0.37, yaw: 0.54 }
  ],
  recoilJitter: 0.090,            // ruido alto: e a arma "erratica" do trio
  recoilRecovery: { speed: 10.0, firingScale: 0.07, delay: 0.05, resetTime: 0.40 },

  kickScale: 0.90,
  shake: { intensity: 0.030, duration: 0.05 },
  adsRecoilScale: 0.85,
  crouchRecoilScale: 0.88,
  airRecoilScale: 1.25,           // perdoa mais o pulo que o fuzil

  muzzleOffset: [0.14, -0.09, -0.40],
  muzzleScale: 0.82,              // cano curto: clarao menor e mais rapido

  sound: {
    fire: 'vp9_fire',
    distant: 'vp9_fire_distant',
    dry: 'weapon_dry',
    reload: 'vp9_reload',
    reloadEmpty: 'vp9_reload_empty',
    reloadEnd: 'vp9_reload_end',
    switch: 'weapon_switch',
    adsIn: 'weapon_ads_in',
    adsOut: 'weapon_ads_out'
  }
};

/** Fuzil semiautomatico de precisao. Um clique, um tiro, uma decisao. */
const SR12 = {
  id: 'sr12',
  name: 'SR-12',
  longName: 'FUZIL DE PRECISAO SR-12',
  class: 'dmr',
  slot: 3,
  hasScope: true,

  fireMode: 'semi',
  rpm: 380,                       // teto mecanico: mesmo clicando rapido, 6.3 tiros/s
  damage: 58,                     // 2 no torso; 1 na cabeca (58 * 3.2 = 185)
  damageFalloff: { start: 55, end: 110, multiplier: 0.80 },
  range: 300,
  pellets: 1,
  penetration: 0.85,              // atravessa madeira e metal fino sem pensar

  magazine: 10,
  reserve: 60,
  reloadTime: 2.45,
  reloadEmptyTime: 3.15,

  switchTime: 0.70,               // pesado de sacar - o preco do dano
  sprintOutTime: 0.26,
  adsTime: 0.34,
  adsFov: 24,                     // zoom forte: ~3.3x contra o FOV padrao de 80
  adsSensitivityScale: 0.45,

  spread: {
    idle: 0.0380,                 // 2.2 graus: no quadril ela e quase inutil, de proposito
    walk: 0.0720,                 // andar atirando com ela e desperdicio de bala
    sprint: 0.0950,
    air: 0.1400,
    crouch: 0.0280,
    ads: 0.00015                  // na mira, parado: onde a mira aponta, a bala vai
  },
  spreadPerShot: 0.0030,
  spreadMax: 0.0100,
  spreadRecovery: 9.0,            // recupera rapido entre cliques
  adsBloomScale: 0.35,
  firstShotAccuracy: 0.02,        // primeiro tiro PERFEITO (nao zero: evita divisao por zero no HUD)

  /**
   * 10 passos, um por bala. Coice pesado e crescente: cada tiro seguinte custa
   * mais caro de corrigir. Yaw pequeno e alternado - ela chuta reto para cima,
   * o lado e so tempero.
   */
  recoilPattern: [
    { pitch: 1.70, yaw: 0.06 },
    { pitch: 1.85, yaw: -0.12 },
    { pitch: 1.95, yaw: 0.16 },
    { pitch: 2.05, yaw: -0.18 },
    { pitch: 2.10, yaw: 0.20 },
    { pitch: 2.15, yaw: -0.22 },
    { pitch: 2.20, yaw: 0.24 },
    { pitch: 2.25, yaw: -0.26 },
    { pitch: 2.30, yaw: 0.28 },
    { pitch: 2.35, yaw: -0.30 }
  ],
  recoilJitter: 0.020,            // quase nenhum: precisao e previsibilidade
  recoilRecovery: { speed: 7.0, firingScale: 0.55, delay: 0.10, resetTime: 0.35 },

  kickScale: 1.25,                // o coice visual e maior que o deslocamento real
  shake: { intensity: 0.20, duration: 0.12 },
  adsRecoilScale: 0.90,
  crouchRecoilScale: 0.80,        // agachar com ela compensa de verdade
  airRecoilScale: 1.50,

  muzzleOffset: [0.15, -0.10, -0.70],
  muzzleScale: 1.45,              // freio de boca: clarao largo e assimetrico

  sound: {
    fire: 'sr12_fire',
    distant: 'sr12_fire_distant',
    dry: 'weapon_dry',
    reload: 'sr12_reload',
    reloadEmpty: 'sr12_reload_empty',
    reloadEnd: 'sr12_reload_end',
    switch: 'weapon_switch',
    adsIn: 'weapon_ads_in',
    adsOut: 'weapon_ads_out'
  }
};

/**
 * ---------------------------------------------------------------------------
 * IDENTIDADE DAS QUATRO ARMAS ACRESCENTADAS (cada uma tapa um buraco do trio)
 * ---------------------------------------------------------------------------
 *   BK-12 espingarda      - 9 chumbos por tiro. Mata em um cartucho a 6 m e nao
 *                           arranha ninguem a 25 m. Vende ALCANCE CURTO ABSOLUTO.
 *   PX-5  pistola         - saca em 0.28 s e recarrega em 1.35 s, as duas coisas
 *                           mais rapidas do arsenal. Vende TEMPO, nao dano.
 *   LM-8  metralhadora    - 80 tiros sem recarregar. Vende VOLUME DE FOGO e
 *                           penetracao; cobra mobilidade, recuo e 6 s de recarga.
 *   MK-4  fuzil tiro-a-tiro - 42 de dano por clique com mira de ferro e boa
 *                           empunhadura de quadril. Vende PONTARIA a media
 *                           distancia, onde o SR-12 e canhao demais e o KR-7 pouco.
 *
 * COMO O EQUILIBRIO FOI FECHADO (nenhuma e estritamente melhor que outra):
 *   tiros no torso para 100 de vida, a queima-roupa e depois de `damageFalloff.end`
 *     KR-7  26  -> 4 perto / 7 longe     VP-9  19  -> 6 perto / 11 longe
 *     SR-12 58  -> 2 perto / 3 longe     BK-12 9x12 -> 1 cartucho perto / nunca longe
 *     PX-5  30  -> 4 perto / 7 longe     LM-8  24  -> 5 perto / 7 longe
 *     MK-4  42  -> 3 perto / 4 longe
 *   um tiro na cabeca (x3.2) mata com dano >= 32: SR-12 em qualquer distancia,
 *   MK-4 so dentro de 22 m, LM-8 e KR-7 nunca. E o que separa o MK-4 do SR-12 sem
 *   dar ao MK-4 o poder do SR-12: ele ganha na cadencia, no saque, no quadril e no
 *   pente, e perde no alcance em que ainda mata de um tiro.
 *
 * TETO DO SERVIDOR QUE ENTRA NA CONTA DA ESPINGARDA: cada chumbo que acerta virou
 * um pacote de acerto (Ballistics emite ENTITY_DAMAGED por chumbo, NetSync manda um
 * `hit` por evento) e o servidor recusa acima de LIMITS.MAX_HITS_PER_SEC = 25.
 * 9 chumbos x 1.2 tiros/s = 10.8 acertos/s, com folga. Subir `pellets` ou `rpm`
 * junto encosta nesse teto e o servidor passa a descartar acerto legitimo.
 */

/**
 * Espingarda de combate, alimentada por pente. Vende ALCANCE CURTO ABSOLUTO:
 * decide o corredor em um cartucho e nao existe a partir de 25 m.
 *
 * `fireMode` E 'semi', NAO 'pump'. O WeaponSystem so distingue 'semi' (exige
 * soltar o gatilho entre tiros); qualquer outro valor cai no ramo automatico, e
 * uma espingarda 'pump' viraria uma automatica de 72 rpm. Como o `rpm` baixo ja
 * da o compasso do bombeamento, 'semi' produz o comportamento certo.
 *
 * PENTE, e nao recarga bala a bala: o WeaponSystem tem UMA recarga, de duracao
 * fixa, que credita o pente inteiro no fim (_finishReload) - nao ha estado de
 * recarga por cartucho para se apoiar, e inventar um exigiria mexer nele. Entao o
 * preco vem em recarga LONGA (3.6 s / 4.4 s, a segunda maior do arsenal).
 */
const BK12 = {
  id: 'bk12',
  name: 'BK-12',
  longName: 'ESPINGARDA DE COMBATE BK-12',
  class: 'shotgun',
  // `slot` e rotulo de inventario: quem troca de arma e o INDICE no array WEAPONS
  // (WeaponSystem._readSwitchInput mapeia Digit1/2/3 -> 0/1/2). Por isso as novas
  // seguem em 4..7 e entram no FIM do array: nenhuma tecla existente muda de arma.
  slot: 4,
  hasScope: false,

  fireMode: 'semi',
  rpm: 72,                        // 1.2 tiros/s -> 833 ms entre cartuchos
  damage: 12,                     // x9 chumbos = 108 a queima-roupa
  damageFalloff: { start: 6, end: 24, multiplier: 0.20 },
  range: 30,                      // alem disso a bala nem e testada contra alvo
  pellets: 9,
  penetration: 0.12,              // chumbo para em qualquer coisa

  magazine: 6,
  reserve: 42,
  reloadTime: 3.60,
  reloadEmptyTime: 4.40,

  switchTime: 0.62,
  sprintOutTime: 0.22,
  adsTime: 0.28,
  adsFov: 74,                     // quase nao aproxima: mirar aqui e alinhar, nao ampliar
  adsSensitivityScale: 0.90,

  spread: {
    idle: 0.0750,                 // 4.3 graus de MEIA-abertura: a 10 m o cone tem 75 cm
    walk: 0.0850,                 // andar quase nao piora - a dispersao ja e o cone
    sprint: 0.1300,
    air: 0.1600,
    crouch: 0.0620,
    ads: 0.0450                   // mirar fecha um terco do cone, nunca o transforma em ponto
  },
  spreadPerShot: 0.0040,
  spreadMax: 0.0180,
  spreadRecovery: 4.0,
  adsBloomScale: 0.60,
  firstShotAccuracy: 0.85,        // espingarda nao tem "tiro perfeito": o cone e o dano

  /**
   * 6 passos, um por cartucho. Coice crescente e alternado.
   * `resetTime` 1.10 s e MAIOR que os 833 ms entre tiros de proposito: quem
   * despeja o pente sem respirar sobe o padrao inteiro; quem espera um segundo
   * entre cartuchos volta ao passo 1 e ao bonus de primeiro tiro.
   */
  recoilPattern: [
    { pitch: 2.60, yaw: 0.30 },
    { pitch: 2.85, yaw: -0.42 },
    { pitch: 3.00, yaw: 0.48 },
    { pitch: 3.10, yaw: -0.52 },
    { pitch: 3.20, yaw: 0.56 },
    { pitch: 3.30, yaw: -0.60 }
  ],
  recoilJitter: 0.100,
  recoilRecovery: { speed: 6.0, firingScale: 0.60, delay: 0.14, resetTime: 1.10 },

  kickScale: 1.60,
  shake: { intensity: 0.260, duration: 0.14 },
  adsRecoilScale: 0.95,           // encostar no ombro quase nao ajuda: e calibre 12
  crouchRecoilScale: 0.85,
  airRecoilScale: 1.40,

  muzzleOffset: [0.16, -0.11, -0.60],
  muzzleScale: 1.70,              // cano de 18.4 mm: o maior clarao do arsenal

  sound: {
    fire: 'bk12_fire',
    distant: 'bk12_fire_distant',
    dry: 'weapon_dry',
    reload: 'bk12_reload',
    reloadEmpty: 'bk12_reload_empty',
    reloadEnd: 'bk12_reload_end',
    switch: 'weapon_switch',
    adsIn: 'weapon_ads_in',
    adsOut: 'weapon_ads_out'
  }
};

/**
 * Pistola. A arma de emergencia: o que ela vende e TEMPO, nao dano. Saque de
 * 0.28 s (menos da metade do KR-7) e recarga de 1.35 s - sair de um pente vazio
 * de fuzil e resolver com a pistola e mais rapido que recarregar o fuzil.
 * Primeiro tiro quase perfeito, recuo leve, e nada de bom depois de 40 m.
 */
const PX5 = {
  id: 'px5',
  name: 'PX-5',
  longName: 'PISTOLA PX-5',
  class: 'pistol',
  slot: 5,
  hasScope: false,

  fireMode: 'semi',
  rpm: 430,                       // 7.2 tiros/s: o dedo e o limite, nao a arma
  damage: 30,                     // 4 no torso perto, 7 longe
  damageFalloff: { start: 18, end: 40, multiplier: 0.50 },
  range: 70,
  pellets: 1,
  penetration: 0.22,

  magazine: 15,
  reserve: 105,
  reloadTime: 1.35,               // a mais rapida do arsenal
  reloadEmptyTime: 1.85,

  switchTime: 0.28,               // O NUMERO QUE DEFINE A ARMA
  sprintOutTime: 0.08,
  adsTime: 0.14,
  adsFov: 66,
  adsSensitivityScale: 0.92,

  spread: {
    idle: 0.0160,                 // melhor que o fuzil no quadril: arma curta e leve
    walk: 0.0230,
    sprint: 0.0620,
    air: 0.0900,                  // a que menos pune atirar pulando
    crouch: 0.0110,
    ads: 0.0016
  },
  spreadPerShot: 0.0026,
  spreadMax: 0.0150,
  spreadRecovery: 7.5,
  adsBloomScale: 0.60,
  firstShotAccuracy: 0.10,        // o primeiro clique acerta onde a mira aponta

  /**
   * 15 passos, um por bala. Teto de 0.90 grau - menor que o do KR-7 (1.02) com
   * cadencia menor: por isso o dobro de tiros seguidos ainda e controlavel.
   * O yaw troca de lado a cada 3-4 tiros, sem os blocos longos do fuzil.
   */
  recoilPattern: [
    { pitch: 0.62, yaw: 0.04 },
    { pitch: 0.78, yaw: -0.10 },
    { pitch: 0.86, yaw: 0.16 },
    { pitch: 0.90, yaw: 0.22 },
    { pitch: 0.88, yaw: 0.12 },
    { pitch: 0.84, yaw: -0.14 },
    { pitch: 0.80, yaw: -0.24 },
    { pitch: 0.78, yaw: -0.18 },
    { pitch: 0.76, yaw: 0.06 },
    { pitch: 0.74, yaw: 0.20 },
    { pitch: 0.72, yaw: 0.26 },
    { pitch: 0.71, yaw: 0.14 },
    { pitch: 0.70, yaw: -0.12 },
    { pitch: 0.69, yaw: -0.22 },
    { pitch: 0.68, yaw: -0.16 }
  ],
  recoilJitter: 0.050,
  // resetTime 0.30 s contra 0.14 s de intervalo: dois tiros colados acumulam,
  // tiros pausados sempre saem do passo 1.
  recoilRecovery: { speed: 11.0, firingScale: 0.45, delay: 0.06, resetTime: 0.30 },

  kickScale: 0.85,
  shake: { intensity: 0.035, duration: 0.05 },
  adsRecoilScale: 0.85,
  crouchRecoilScale: 0.88,
  airRecoilScale: 1.20,

  muzzleOffset: [0.13, -0.09, -0.30],
  muzzleScale: 0.90,

  sound: {
    fire: 'px5_fire',
    distant: 'px5_fire_distant',
    dry: 'weapon_dry',
    reload: 'px5_reload',
    reloadEmpty: 'px5_reload_empty',
    reloadEnd: 'px5_reload_end',
    switch: 'weapon_switch',
    adsIn: 'weapon_ads_in',
    adsOut: 'weapon_ads_out'
  }
};

/**
 * Metralheira leve. Vende VOLUME DE FOGO: 80 tiros no tambor, 13.3 por segundo e
 * penetracao de 0.62 (atravessa a cobertura fina que salva quem usa VP-9). Cobra
 * o preco todo em tempo e em mobilidade.
 *
 * MOBILIDADE RUIM SE ESCREVE COM OS CAMPOS QUE EXISTEM: a tabela de armas nao tem
 * multiplicador de velocidade de caminhada (PLAYER.WALK_SPEED e global, em
 * Constants.js). O que da o peso e: switchTime 0.95, sprintOutTime 0.38 (quase
 * meio segundo de arma subindo ao sair da corrida), adsTime 0.42, spread.sprint e
 * spread.air altissimos e airRecoilScale 1.60 - atirar correndo ou pulando com ela
 * e jogar bala fora. Em compensacao crouchRecoilScale 0.72 e o maior premio de
 * agachar do arsenal: parada e agachada, ela e a arma mais forte do jogo.
 *
 * Os tempos de recarga param em 4.90 s / 5.95 s por um motivo TECNICO, nao de
 * balanceamento: o ViewModel limita a animacao a [0.4, 6] s (_onReloadStart) e a
 * [0.5, 5] / [0.6, 6] s nos padroes (_applyTimings). Passar disso deixaria a arma
 * de volta em repouso com a recarga ainda correndo.
 */
const LM8 = {
  id: 'lm8',
  name: 'LM-8',
  longName: 'METRALHADORA LEVE LM-8',
  class: 'lmg',
  slot: 6,
  hasScope: false,

  fireMode: 'auto',
  rpm: 800,                       // 13.3 tiros/s -> o tambor inteiro em 6.0 s
  damage: 24,                     // 5 no torso perto: um a mais que o KR-7
  damageFalloff: { start: 24, end: 60, multiplier: 0.60 },
  range: 130,
  pellets: 1,
  penetration: 0.62,              // a segunda maior, atras so do SR-12

  magazine: 80,
  reserve: 240,
  reloadTime: 4.90,
  reloadEmptyTime: 5.95,          // a recarga mais longa do jogo, de longe

  switchTime: 0.95,               // a mais pesada de sacar
  sprintOutTime: 0.38,
  adsTime: 0.42,
  adsFov: 56,
  adsSensitivityScale: 0.80,

  spread: {
    idle: 0.0330,                 // 1.9 grau: no quadril ela varre, nao acerta
    walk: 0.0520,
    sprint: 0.0980,
    air: 0.1350,
    crouch: 0.0180,               // agachar corta a dispersao quase pela metade
    ads: 0.0022
  },
  spreadPerShot: 0.0030,
  spreadMax: 0.0280,              // o maior teto de dispersao acumulada do arsenal
  spreadRecovery: 3.8,            // e o mais lento a fechar: rajada longa custa caro
  adsBloomScale: 0.80,
  firstShotAccuracy: 0.55,

  /**
   * 80 passos, um por bala. Tres regimes, e e neles que mora o "rajada curta":
   *  1-3    salto seco de 1.05 a 1.32 grau, quase sem lado - a rajada de tres e
   *         a unica que sai onde a mira esta;
   *  4-20   o pitch cai para 0.68 e o yaw comeca a varrer;
   *  21-80  varredura em blocos de 8 tiros trocando de lado, com amplitude 0.94 -
   *         50% mais larga que a do VP-9. De cima o traco desenha um zigue-zague
   *         de dente de serra: da para decorar, nao da para corrigir na mao.
   */
  recoilPattern: [
    { pitch: 1.05, yaw: 0.00 },
    { pitch: 1.22, yaw: 0.06 },
    { pitch: 1.32, yaw: -0.10 },
    { pitch: 1.28, yaw: 0.14 },
    { pitch: 1.20, yaw: 0.28 },
    { pitch: 1.12, yaw: 0.48 },
    { pitch: 1.05, yaw: 0.66 },
    { pitch: 0.99, yaw: 0.82 },
    { pitch: 0.94, yaw: 0.92 },
    { pitch: 0.90, yaw: 0.88 },
    { pitch: 0.86, yaw: 0.70 },
    { pitch: 0.83, yaw: 0.40 },
    { pitch: 0.80, yaw: 0.02 },
    { pitch: 0.78, yaw: -0.38 },
    { pitch: 0.76, yaw: -0.68 },
    { pitch: 0.74, yaw: -0.86 },
    { pitch: 0.72, yaw: -0.94 },
    { pitch: 0.70, yaw: -0.90 },
    { pitch: 0.69, yaw: -0.72 },
    { pitch: 0.68, yaw: -0.42 },
    { pitch: 0.66, yaw: -0.04 },
    { pitch: 0.65, yaw: 0.36 },
    { pitch: 0.64, yaw: 0.66 },
    { pitch: 0.63, yaw: 0.84 },
    { pitch: 0.62, yaw: 0.92 },
    { pitch: 0.61, yaw: 0.88 },
    { pitch: 0.60, yaw: 0.70 },
    { pitch: 0.59, yaw: 0.40 },
    { pitch: 0.58, yaw: 0.02 },
    { pitch: 0.58, yaw: -0.36 },
    { pitch: 0.57, yaw: -0.66 },
    { pitch: 0.56, yaw: -0.84 },
    { pitch: 0.56, yaw: -0.92 },
    { pitch: 0.55, yaw: -0.88 },
    { pitch: 0.54, yaw: -0.70 },
    { pitch: 0.54, yaw: -0.40 },
    { pitch: 0.53, yaw: -0.02 },
    { pitch: 0.53, yaw: 0.34 },
    { pitch: 0.52, yaw: 0.64 },
    { pitch: 0.52, yaw: 0.82 },
    { pitch: 0.51, yaw: 0.90 },
    { pitch: 0.51, yaw: 0.86 },
    { pitch: 0.50, yaw: 0.68 },
    { pitch: 0.50, yaw: 0.38 },
    { pitch: 0.49, yaw: 0.00 },
    { pitch: 0.49, yaw: -0.34 },
    { pitch: 0.49, yaw: -0.64 },
    { pitch: 0.48, yaw: -0.82 },
    { pitch: 0.48, yaw: -0.90 },
    { pitch: 0.48, yaw: -0.86 },
    { pitch: 0.47, yaw: -0.68 },
    { pitch: 0.47, yaw: -0.38 },
    { pitch: 0.47, yaw: 0.00 },
    { pitch: 0.46, yaw: 0.32 },
    { pitch: 0.46, yaw: 0.62 },
    { pitch: 0.46, yaw: 0.80 },
    { pitch: 0.45, yaw: 0.88 },
    { pitch: 0.45, yaw: 0.84 },
    { pitch: 0.45, yaw: 0.66 },
    { pitch: 0.45, yaw: 0.36 },
    { pitch: 0.44, yaw: -0.02 },
    { pitch: 0.44, yaw: -0.32 },
    { pitch: 0.44, yaw: -0.62 },
    { pitch: 0.44, yaw: -0.80 },
    { pitch: 0.43, yaw: -0.88 },
    { pitch: 0.43, yaw: -0.84 },
    { pitch: 0.43, yaw: -0.66 },
    { pitch: 0.43, yaw: -0.36 },
    { pitch: 0.43, yaw: 0.02 },
    { pitch: 0.42, yaw: 0.30 },
    { pitch: 0.42, yaw: 0.60 },
    { pitch: 0.42, yaw: 0.78 },
    { pitch: 0.42, yaw: 0.86 },
    { pitch: 0.42, yaw: 0.82 },
    { pitch: 0.41, yaw: 0.64 },
    { pitch: 0.41, yaw: 0.34 },
    { pitch: 0.41, yaw: 0.00 },
    { pitch: 0.41, yaw: -0.30 },
    { pitch: 0.41, yaw: -0.58 },
    { pitch: 0.41, yaw: -0.76 }
  ],
  recoilJitter: 0.075,
  // firingScale 0.045: durante a rajada o padrao praticamente NAO volta. delay e
  // resetTime longos: depois de soltar, ela ainda segura no alto por um instante.
  recoilRecovery: { speed: 6.5, firingScale: 0.045, delay: 0.11, resetTime: 0.55 },

  kickScale: 1.15,
  shake: { intensity: 0.075, duration: 0.08 },
  adsRecoilScale: 0.80,
  crouchRecoilScale: 0.72,        // o maior premio de agachar do arsenal
  airRecoilScale: 1.60,           // e a maior punicao por atirar no ar

  muzzleOffset: [0.17, -0.12, -0.72],
  muzzleScale: 1.30,

  sound: {
    fire: 'lm8_fire',
    distant: 'lm8_fire_distant',
    dry: 'weapon_dry',
    reload: 'lm8_reload',
    reloadEmpty: 'lm8_reload_empty',
    reloadEnd: 'lm8_reload_end',
    switch: 'weapon_switch',
    adsIn: 'weapon_ads_in',
    adsOut: 'weapon_ads_out'
  }
};

/**
 * Fuzil de batalha semiautomatico. Vende PONTARIA A MEDIA DISTANCIA: um clique,
 * 42 de dano, mira de ferro e sem luneta. Tres tiros no torso, e um na cabeca
 * mata - mas SO dentro de 22 m, que e onde a queda de dano comeca.
 *
 * COMO ELE NAO PISA NO SR-12: o SR-12 mata de um tiro na cabeca em qualquer
 * distancia (58 x 3.2 = 185) e de dois no torso ate 55 m, com luneta de 24 graus.
 * O MK-4 troca isso por manejo: saca em 0.45 s (contra 0.70), mira em 0.22 s
 * (contra 0.34), tem dispersao de quadril tres vezes menor, 20 tiros no pente
 * (contra 10) e nao entra em tunel de luneta a 5 m de distancia. Depois de 48 m o
 * dano cai para 26 e a cabeca deixa de matar de um tiro - a partir dali o SR-12 e
 * a arma certa, e o MK-4 esta fora do lugar dele.
 */
const MK4 = {
  id: 'mk4',
  name: 'MK-4',
  longName: 'FUZIL SEMIAUTOM\u00c1TICO MK-4',
  class: 'battle',
  slot: 7,
  hasScope: false,

  fireMode: 'semi',
  rpm: 400,                       // 6.7 tiros/s: quem limita e o dedo
  damage: 42,                     // 3 no torso perto; cabeca (134) mata dentro de 22 m
  damageFalloff: { start: 22, end: 48, multiplier: 0.62 },
  range: 110,
  pellets: 1,
  penetration: 0.58,

  magazine: 20,
  reserve: 120,
  reloadTime: 2.20,
  reloadEmptyTime: 2.90,

  switchTime: 0.45,
  sprintOutTime: 0.20,
  adsTime: 0.22,
  adsFov: 44,                     // mais fechado que o KR-7 e muito mais aberto que a luneta
  adsSensitivityScale: 0.70,

  spread: {
    idle: 0.0250,                 // pior que o KR-7 no quadril, tres vezes melhor que o SR-12
    walk: 0.0430,
    sprint: 0.0850,
    air: 0.1250,
    crouch: 0.0175,
    ads: 0.0004                   // na mira, parado, e um ponto
  },
  spreadPerShot: 0.0032,
  spreadMax: 0.0130,
  spreadRecovery: 8.5,            // fecha rapido entre cliques: o ritmo e recompensado
  adsBloomScale: 0.40,
  firstShotAccuracy: 0.05,        // primeiro tiro impecavel

  /**
   * 20 passos, um por bala. So SUBIDA: pitch de 1.15 a 1.94 grau, crescente, com
   * yaw minusculo alternando de lado. O padrao nao serpenteia - ele empurra a mira
   * reto para cima e cada clique seguido custa mais caro que o anterior. Com
   * firingScale 0.60 (o dobro do KR-7) o recuo VOLTA de verdade entre tiros: quem
   * espera o compasso atira sempre do mesmo ponto, quem martela o botao sobe.
   */
  recoilPattern: [
    { pitch: 1.15, yaw: 0.05 },
    { pitch: 1.32, yaw: -0.09 },
    { pitch: 1.44, yaw: 0.12 },
    { pitch: 1.52, yaw: -0.14 },
    { pitch: 1.58, yaw: 0.16 },
    { pitch: 1.62, yaw: -0.18 },
    { pitch: 1.66, yaw: 0.19 },
    { pitch: 1.70, yaw: -0.21 },
    { pitch: 1.72, yaw: 0.22 },
    { pitch: 1.74, yaw: -0.24 },
    { pitch: 1.76, yaw: 0.25 },
    { pitch: 1.78, yaw: -0.26 },
    { pitch: 1.80, yaw: 0.27 },
    { pitch: 1.82, yaw: -0.28 },
    { pitch: 1.84, yaw: 0.29 },
    { pitch: 1.86, yaw: -0.30 },
    { pitch: 1.88, yaw: 0.31 },
    { pitch: 1.90, yaw: -0.32 },
    { pitch: 1.92, yaw: 0.33 },
    { pitch: 1.94, yaw: -0.34 }
  ],
  recoilJitter: 0.030,            // previsivel de proposito: e arma de decorar
  recoilRecovery: { speed: 8.0, firingScale: 0.60, delay: 0.09, resetTime: 0.34 },

  kickScale: 1.20,
  shake: { intensity: 0.120, duration: 0.10 },
  adsRecoilScale: 0.85,
  crouchRecoilScale: 0.78,
  airRecoilScale: 1.45,

  muzzleOffset: [0.15, -0.10, -0.65],
  muzzleScale: 1.25,

  sound: {
    fire: 'mk4_fire',
    distant: 'mk4_fire_distant',
    dry: 'weapon_dry',
    reload: 'mk4_reload',
    reloadEmpty: 'mk4_reload_empty',
    reloadEnd: 'mk4_reload_end',
    switch: 'weapon_switch',
    adsIn: 'weapon_ads_in',
    adsOut: 'weapon_ads_out'
  }
};

/**
 * Ordem do array = ordem do INVENTARIO.
 *
 * Os indices 0, 1 e 2 continuam sendo as teclas Digit1/2/3 porque quem troca de
 * arma le o INDICE deste array, nao o campo `slot` (WeaponSystem._readSwitchInput
 * chama _requestSwitch(0|1|2); o campo `slot` nao e lido por nenhum modulo). Por
 * isso as quatro armas novas entram NO FIM, nos indices 3 a 6: reordenar aqui
 * trocaria a arma de cada tecla ja existente.
 *
 * As novas sao alcancaveis por weapons.switchTo(id ou indice) e pela tecla Q
 * (ultima arma usada). Para elas terem tecla propria falta bindings weapon4..7 em
 * src/core/Input.js e a leitura correspondente em WeaponSystem._readSwitchInput -
 * nenhum dos dois arquivos pertence a este lote.
 */
export const WEAPONS = [KR7, VP9, SR12, BK12, PX5, LM8, MK4];

/** Busca por id. Devolve null se nao existir - nunca lanca. */
export function getWeapon(id) {
  for (let i = 0; i < WEAPONS.length; i++) {
    if (WEAPONS[i].id === id) return WEAPONS[i];
  }
  return null;
}

export default WEAPONS;
